import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { bomApi, type BomItemView, type PositionDetail } from "../../api/structure";
import type { ApiError } from "../../api/client";
import { truncate } from "../../lib/format";
import { formatQuantityWithUnit } from "../../lib/quantity";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { ContextMenu, type ContextMenuItem } from "../../components/ContextMenu";
import { PermLink } from "../../components/PermLink";
import { TextPopover } from "../../components/TextPopover";
import Ic from "../../components/icons.jsx";
import { BomItemModal, type BomItemModalMode } from "./BomItemModal";

/** "Učitavanje…" only when the first list takes longer than this (blueprint 09 §2). */
const LOADING_DELAY_MS = 150;
/** A note shown inline beside the article name on a tablet (the Napomena column is hidden there). */
const NOTE_INLINE_MAX = 80;

/** "ŠIFRA — Naziv" of the line's article, as the history names it. */
function itemLabel(it: BomItemView): string {
  return [it.articleCode, it.articleName].filter(Boolean).join(" — ") || "—";
}

/** Server order: sortOrder, then article code. */
function sortItems(list: BomItemView[]): BomItemView[] {
  return [...list].sort((a, b) => a.sortOrder - b.sortOrder || (a.articleCode ?? "").localeCompare(b.articleCode ?? ""));
}

/**
 * "Sastavnica" on the position card (sprint 5l §4, §7): the articles that go on the position,
 * each with a quantity in the article's unit and a note. Shown for every kind of position;
 * hides itself without `bom:view` (the page does not need to know). The list is fetched only
 * when the card says there is something (`bomCount`), stays mounted and dims while it refreshes
 * (hard rule 11); writes update the rows in place and tell the page to refresh the history.
 */
export function BomSection({
  detail, canCreate, canEdit, canDelete, onChanged,
}: {
  detail: PositionDetail;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  /** After every write (the position's history has a new row). */
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const canView = hasPermission("bom:view");
  const canCreateArticle = hasPermission("articles:create");
  const { id: positionId, bomCount, deletedAt } = detail;
  // A deleted position's sastavnica is readable, never written (the server answers 404 to writes).
  const readOnly = !!deletedAt;

  const [items, setItems] = useState<BomItemView[] | null>(bomCount === 0 ? [] : null);
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [modal, setModal] = useState<BomItemModalMode | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; item: BomItemView } | null>(null);

  useEffect(() => {
    if (!canView) return;
    if (bomCount === 0) { setItems([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    bomApi.list(positionId)
      .then((list) => { if (!cancelled) setItems(sortItems(list)); })
      .catch((err: ApiError) => { if (!cancelled) { setItems([]); toast.error(err.message); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [positionId, bomCount, canView, toast]);

  // Nothing old to keep on screen: the placeholder appears only after a real wait.
  useEffect(() => {
    if (!(loading && items === null)) { setShowLoading(false); return; }
    const h = setTimeout(() => setShowLoading(true), LOADING_DELAY_MS);
    return () => clearTimeout(h);
  }, [loading, items]);

  function handleSaved(item: BomItemView, created: boolean) {
    setModal(null);
    setItems((prev) => sortItems(created ? [...(prev ?? []), item] : (prev ?? []).map((x) => (x.id === item.id ? item : x))));
    toast.success(created ? t("bom.toast.added", "Stavka dodata.") : t("bom.toast.saved", "Stavka sačuvana."));
    onChanged();
  }

  /** 409 ARTICLE_TAKEN from the add dialog: open the line that already carries the article. */
  async function editExisting(itemId: string) {
    let item = items?.find((x) => x.id === itemId);
    if (!item) {
      try {
        const list = sortItems(await bomApi.list(positionId));
        setItems(list);
        item = list.find((x) => x.id === itemId);
      } catch (err) {
        toast.error((err as ApiError).message);
      }
    }
    setModal(item ? { kind: "edit", item } : null);
  }

  async function remove(item: BomItemView) {
    const ok = await confirm({
      title: t("bom.confirmDelete.title", "Uklanjanje stavke sa sastavnice"),
      message: t("bom.confirmDelete.message", "Ukloniti „{{name}}“ sa sastavnice?", { name: itemLabel(item) }),
      confirmLabel: t("action.remove", "Ukloni"),
      danger: true,
    });
    if (!ok) return;
    try {
      await bomApi.remove(positionId, item.id);
      setItems((prev) => (prev ?? []).filter((x) => x.id !== item.id));
      toast.success(t("bom.toast.deleted", "Stavka uklonjena."));
      onChanged();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  if (!canView) return null;

  const hasActions = !readOnly && (canEdit || canDelete);
  const colCode = t("bom.col.code", "Šifra");
  const colName = t("bom.col.name", "Artikal");
  const colType = t("bom.col.type", "Tip");
  const colQuantity = t("bom.col.quantity", "Količina");
  const colNote = t("bom.col.note", "Napomena");
  const colActions = t("common.actions", "Akcije");
  const dash = <span className="muted">—</span>;

  const menuItems: ContextMenuItem[] = menu ? [
    ...(canEdit ? [{ label: t("action.edit", "Izmeni"), icon: <Ic.pencil />, onSelect: () => setModal({ kind: "edit", item: menu.item }) }] : []),
    ...(canDelete ? [{ label: t("action.remove", "Ukloni"), icon: <Ic.x />, danger: true, separator: canEdit, onSelect: () => { void remove(menu.item); } }] : []),
  ] : [];

  return (
    <section className="card">
      <div className="card-h">
        <div>
          <h3>{t("bom.section.title", "Sastavnica")}</h3>
          <div className="sub">
            {items && items.length > 0
              ? t("bom.section.count", "Artikli sa količinom koji idu na ovu poziciju ({{count}}).", { count: items.length })
              : t("bom.section.sub", "Količinski delovi koji idu na ovu poziciju — artikli iz kataloga sa količinom.")}
          </div>
        </div>
        {canCreate && !readOnly && (
          <button type="button" className="btn sm" onClick={() => setModal({ kind: "add" })}>
            <Ic.plus /> {t("bom.add", "Dodaj stavku")}
          </button>
        )}
      </div>
      {items === null ? (
        <div className="empty" aria-busy="true">{showLoading ? t("common.loading", "Učitavanje…") : " "}</div>
      ) : items.length === 0 ? (
        <div className="empty">{t("bom.section.empty", "Sastavnica je prazna.")}</div>
      ) : (
        <div className="tbl-wrap" style={{ opacity: loading ? 0.6 : 1, transition: "opacity .15s" }} aria-busy={loading}>
          <table className="tbl list-card" aria-label={t("bom.section.title", "Sastavnica")}>
            <thead>
              <tr>
                <th>{colCode}</th>
                <th>{colName}</th>
                <th className="hide-tablet">{colType}</th>
                <th className="num">{colQuantity}</th>
                <th className="hide-tablet">{colNote}</th>
                {hasActions && <th aria-label={colActions} />}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td className="mono" data-label={colCode}>
                    {it.articleCode
                      ? <PermLink permission="articles:view" to={`/artikli/${it.articleId}`}>{it.articleCode}</PermLink>
                      : dash}
                  </td>
                  <td data-label={colName}>
                    <b>{it.articleName ?? "—"}</b>
                    {it.articleDeleted && <span className="tag neutral" style={{ marginLeft: 8 }}>{t("bom.articleDeleted", "obrisan artikal")}</span>}
                    {/* Tablet band: the Tip and Napomena columns are hidden, so both read here beside the name. */}
                    {it.articleTypeName && <span className="show-tablet muted" style={{ marginLeft: 8, fontSize: 12 }}>{it.articleTypeName}</span>}
                    {it.note && <span className="show-tablet muted" style={{ marginLeft: 8, fontSize: 12 }} title={it.note}>· {truncate(it.note, NOTE_INLINE_MAX)}</span>}
                  </td>
                  <td className="hide-tablet" data-label={colType}>{it.articleTypeName ?? dash}</td>
                  <td className="num" data-label={colQuantity}>{formatQuantityWithUnit(it.quantity, it.unit)}</td>
                  <td className="hide-tablet cell-note" data-label={colNote}>{it.note ? <TextPopover text={it.note} /> : dash}</td>
                  {hasActions && (
                    <td className="card-actions" style={{ textAlign: "right", whiteSpace: "nowrap" }} data-label={colActions}>
                      <button
                        type="button"
                        className="btn sm"
                        aria-label={t("bom.rowActions", "Radnje za stavku: {{name}}", { name: itemLabel(it) })}
                        data-tip={t("structure.moreActions", "Više akcija")}
                        aria-haspopup="menu"
                        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: r.left, y: r.bottom + 4, item: it }); }}
                      >
                        <Ic.more />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {menu && menuItems.length > 0 && <ContextMenu x={menu.x} y={menu.y} items={menuItems} onClose={() => setMenu(null)} />}
      {modal && (
        // Keyed by mode: the 409 → "Izmeni postojeću stavku" swap must start from the existing line, not the typed draft.
        <BomItemModal
          key={modal.kind === "edit" ? `edit-${modal.item.id}` : "add"}
          positionId={positionId}
          mode={modal}
          canCreateArticle={canCreateArticle}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
          onEditExisting={(itemId) => { void editExisting(itemId); }}
        />
      )}
    </section>
  );
}
